import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Search, ShoppingCart, Menu, ChevronDown, X, MapPin, User } from "lucide-react";

export const Header = () => {
  const [departments, setDepartments] = useState({});
  const [active, setActive] = useState(null);
  const [query, setQuery] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const { count, setOpen } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const closeTimer = useRef(null);

  useEffect(() => {
    axios.get(`${API}/catalog/departments`).then((r) => setDepartments(r.data)).catch(() => {});
  }, []);

  const submitSearch = (e) => {
    e.preventDefault();
    if (query.trim()) navigate(`/catalogue?search=${encodeURIComponent(query.trim())}`);
  };

  const openMenu = (d) => {
    clearTimeout(closeTimer.current);
    setActive(d);
  };
  const scheduleClose = () => {
    closeTimer.current = setTimeout(() => setActive(null), 150);
  };

  const depNames = Object.keys(departments);

  return (
    <header className="sticky top-0 z-50 w-full border-b border-slate-200 bg-white/90 backdrop-blur-xl">
      {/* top bar */}
      <div className="hidden bg-slate-900 text-white md:block">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-1.5 text-xs">
          <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> 5 rue des enclos, 35350 La Gouesnière</span>
          <span>Paiement 100% sécurisé · Livraison en France & Benelux</span>
        </div>
      </div>

      <div className="mx-auto flex max-w-7xl items-center gap-4 px-4 py-3 md:px-6">
        <button className="md:hidden" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-button">
          <Menu className="h-6 w-6 text-slate-800" />
        </button>

        <Link to="/" data-testid="logo-link" className="flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-600 font-head text-lg font-extrabold text-white">JC</div>
          <span className="font-head text-xl font-extrabold tracking-tight text-slate-900">BOUTIK</span>
        </Link>

        <form onSubmit={submitSearch} className="relative ml-2 hidden flex-1 md:block" data-testid="search-form">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher un produit, une marque, un SKU..."
            className="h-12 rounded-full border-slate-200 bg-slate-50 pl-12 pr-4 text-base focus-visible:ring-blue-500"
          />
        </form>

        {user?.is_admin && (
          <button
            data-testid="admin-link"
            onClick={() => navigate("/admin")}
            className="ml-auto hidden rounded-full bg-slate-900 px-3 py-2 text-sm font-semibold text-white hover:bg-slate-700 sm:block"
          >
            Admin
          </button>
        )}

        <button
          data-testid="account-button"
          onClick={() => navigate("/compte")}
          className={`hidden items-center gap-2 rounded-full px-3 py-2 hover:bg-slate-100 sm:flex ${user?.is_admin ? "" : "ml-auto"}`}
        >
          <User className="h-6 w-6 text-slate-800" />
          <span className="hidden max-w-[120px] truncate text-sm font-semibold text-slate-800 lg:inline">
            {user ? user.name?.split(" ")[0] : "Mon compte"}
          </span>
        </button>

        <button
          data-testid="cart-button"
          onClick={() => setOpen(true)}
          className="relative flex items-center gap-2 rounded-full px-3 py-2 hover:bg-slate-100 sm:ml-0 ml-auto"
        >
          <ShoppingCart className="h-6 w-6 text-slate-800" />
          {count > 0 && (
            <span className="absolute -right-0 -top-0 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-600 px-1 text-[11px] font-bold text-white" data-testid="cart-count">
              {count}
            </span>
          )}
          <span className="hidden text-sm font-semibold text-slate-800 lg:inline">Panier</span>
        </button>
      </div>

      {/* mobile search */}
      <form onSubmit={submitSearch} className="px-4 pb-3 md:hidden">
        <div className="relative">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="search-input-mobile"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher un produit..."
            className="h-11 rounded-full border-slate-200 bg-slate-50 pl-12"
          />
        </div>
      </form>

      {/* mobile rayons strip */}
      <div className="jc-scroll flex gap-2 overflow-x-auto border-t border-slate-100 px-4 py-2.5 md:hidden" data-testid="mobile-rayons-strip">
        {depNames.map((d) => (
          <button
            key={d}
            data-testid={`mobile-dept-${d}`}
            onClick={() => navigate(`/catalogue?department=${encodeURIComponent(d)}`)}
            className="whitespace-nowrap rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-sm font-semibold text-slate-700 active:bg-blue-50"
          >
            {d}
          </button>
        ))}
      </div>

      {/* mega menu nav */}
      <nav className="relative z-40 hidden border-t border-slate-100 md:block" onMouseLeave={scheduleClose}>
        <div className="mx-auto flex max-w-7xl items-stretch gap-1 px-6">
          {depNames.map((d) => (
            <div key={d} onMouseEnter={() => openMenu(d)} className="relative">
              <button
                data-testid={`dept-${d}`}
                onClick={() => navigate(`/catalogue?department=${encodeURIComponent(d)}`)}
                className={`flex items-center gap-1 py-3 text-sm font-semibold transition-colors ${active === d ? "text-blue-700" : "text-slate-700 hover:text-blue-700"}`}
              >
                {d}
                <ChevronDown className={`h-4 w-4 transition-transform ${active === d ? "rotate-180" : ""}`} />
              </button>
            </div>
          ))}
        </div>

        {active && departments[active] && (
          <div
            onMouseEnter={() => openMenu(active)}
            className="absolute left-0 right-0 top-full z-50 border-t border-slate-100 bg-white shadow-xl"
          >
            <div className="mx-auto grid max-w-7xl grid-cols-2 gap-x-8 gap-y-2 px-6 py-6 lg:grid-cols-4">
              <div className="col-span-2 lg:col-span-1">
                <h4 className="font-head text-lg font-bold text-slate-900">{active}</h4>
                <p className="mt-1 text-sm text-slate-500">Nos meilleures sélections du rayon.</p>
                <button
                  onClick={() => { navigate(`/catalogue?department=${encodeURIComponent(active)}`); setActive(null); }}
                  className="mt-3 text-sm font-semibold text-blue-700 hover:underline"
                >
                  Voir tout le rayon →
                </button>
              </div>
              {departments[active].map((c) => (
                <button
                  key={c}
                  data-testid={`subcat-${c}`}
                  onClick={() => { navigate(`/catalogue?category=${encodeURIComponent(c)}`); setActive(null); }}
                  className="truncate py-1 text-left text-sm text-slate-600 hover:text-blue-700"
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
        )}
      </nav>

      {/* mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-80 max-w-[85%] overflow-y-auto bg-white p-5">
            <div className="mb-4 flex items-center justify-between">
              <span className="font-head text-lg font-extrabold">Rayons</span>
              <button onClick={() => setMobileOpen(false)}><X className="h-6 w-6" /></button>
            </div>
            {depNames.map((d) => (
              <button
                key={d}
                onClick={() => { navigate(`/catalogue?department=${encodeURIComponent(d)}`); setMobileOpen(false); }}
                className="block w-full border-b border-slate-100 py-3 text-left font-semibold text-slate-700"
              >
                {d}
              </button>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};
